/**
 * Canonical D-Tools system/room block reordering.
 * Authority is line sortOrder — not navigator display arrays.
 */
import { resolveDtoolsSystemContentKey } from "./dtoolsProposalContent";

export type DtoolsHierarchyOrderDirection = "up" | "down";

export type DtoolsHierarchyOrderLine = {
  id: string;
  systemId?: string | null;
  systemName?: string | null;
  locationId?: string | null;
  roomName?: string | null;
  parentId?: string | null;
  sortOrder: number;
};

function normalizeLabel(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/** Stable system identity: prefer IDs, fall back to normalised name for legacy rows. */
export function dtoolsSystemIdentity(
  line: Pick<DtoolsHierarchyOrderLine, "systemId" | "systemName">,
): string {
  return resolveDtoolsSystemContentKey({
    systemId: line.systemId ?? null,
    name: String(line.systemName ?? "").trim() || "Unassigned system",
  });
}

/**
 * Stable room identity within a system.
 * Prefer locationId; fall back to normalised room name. Null = system-level / unassigned room.
 */
export function dtoolsRoomIdentity(
  line: Pick<DtoolsHierarchyOrderLine, "locationId" | "roomName">,
): string | null {
  const locationId = String(line.locationId ?? "").trim();
  if (locationId) return `id:${locationId}`;
  const roomName = String(line.roomName ?? "").trim();
  if (!roomName) return null;
  return `name:${normalizeLabel(roomName)}`;
}

export function normaliseDtoolsHierarchySortOrder<T extends { sortOrder: number }>(lines: T[]): T[] {
  return lines.map((line, index) => ({ ...line, sortOrder: index }));
}

function sortedBySortOrder<T extends DtoolsHierarchyOrderLine>(lines: T[]): T[] {
  return [...lines].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id.localeCompare(b.id),
  );
}

function listSystemIdentitiesInOrder<T extends DtoolsHierarchyOrderLine>(lines: T[]): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const line of sortedBySortOrder(lines)) {
    const identity = dtoolsSystemIdentity(line);
    if (seen.has(identity)) continue;
    seen.add(identity);
    order.push(identity);
  }
  return order;
}

function listRoomIdentitiesInOrder<T extends DtoolsHierarchyOrderLine>(
  lines: T[],
  systemIdentity: string,
): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const line of sortedBySortOrder(lines)) {
    if (dtoolsSystemIdentity(line) !== systemIdentity) continue;
    const room = dtoolsRoomIdentity(line);
    if (!room || seen.has(room)) continue;
    seen.add(room);
    order.push(room);
  }
  return order;
}

function swapAdjacent<T>(items: T[], index: number, direction: DtoolsHierarchyOrderDirection): T[] {
  const target = direction === "up" ? index - 1 : index + 1;
  if (index < 0 || target < 0 || index >= items.length || target >= items.length) return items;
  const next = [...items];
  const tmp = next[index]!;
  next[index] = next[target]!;
  next[target] = tmp;
  return next;
}

export function canMoveDtoolsSystem<T extends DtoolsHierarchyOrderLine>(
  lines: T[],
  systemIdentity: string,
  direction: DtoolsHierarchyOrderDirection,
): boolean {
  const order = listSystemIdentitiesInOrder(lines);
  const index = order.indexOf(systemIdentity);
  if (index < 0) return false;
  if (direction === "up") return index > 0;
  return index < order.length - 1;
}

export function moveDtoolsSystem<T extends DtoolsHierarchyOrderLine>(
  lines: T[],
  systemIdentity: string,
  direction: DtoolsHierarchyOrderDirection,
): T[] {
  if (!canMoveDtoolsSystem(lines, systemIdentity, direction)) return lines;
  const ordered = sortedBySortOrder(lines);
  const systemOrder = listSystemIdentitiesInOrder(ordered);
  const index = systemOrder.indexOf(systemIdentity);
  const nextSystemOrder = swapAdjacent(systemOrder, index, direction);

  const blocks = new Map<string, T[]>();
  for (const identity of nextSystemOrder) blocks.set(identity, []);
  for (const line of ordered) {
    const identity = dtoolsSystemIdentity(line);
    const block = blocks.get(identity);
    if (block) block.push(line);
    else {
      // Defensive: unknown identity appended as its own trailing block.
      blocks.set(identity, [line]);
      nextSystemOrder.push(identity);
    }
  }

  const flattened: T[] = [];
  for (const identity of nextSystemOrder) {
    flattened.push(...(blocks.get(identity) ?? []));
  }
  return normaliseDtoolsHierarchySortOrder(flattened);
}

type SystemSegment<T> =
  | { kind: "room"; identity: string; lines: T[] }
  | { kind: "systemLevel"; lines: T[] };

function buildSystemSegments<T extends DtoolsHierarchyOrderLine>(
  systemLines: T[],
): SystemSegment<T>[] {
  const segments: SystemSegment<T>[] = [];
  for (const line of systemLines) {
    const room = dtoolsRoomIdentity(line);
    const last = segments[segments.length - 1];
    if (room == null) {
      if (last?.kind === "systemLevel") last.lines.push(line);
      else segments.push({ kind: "systemLevel", lines: [line] });
      continue;
    }
    if (last?.kind === "room" && last.identity === room) {
      last.lines.push(line);
    } else {
      segments.push({ kind: "room", identity: room, lines: [line] });
    }
  }
  return segments;
}

export function canMoveDtoolsRoom<T extends DtoolsHierarchyOrderLine>(
  lines: T[],
  systemIdentity: string,
  roomIdentity: string,
  direction: DtoolsHierarchyOrderDirection,
): boolean {
  const roomOrder = listRoomIdentitiesInOrder(lines, systemIdentity);
  const index = roomOrder.indexOf(roomIdentity);
  if (index < 0) return false;
  if (direction === "up") return index > 0;
  return index < roomOrder.length - 1;
}

export function moveDtoolsRoom<T extends DtoolsHierarchyOrderLine>(
  lines: T[],
  systemIdentity: string,
  roomIdentity: string,
  direction: DtoolsHierarchyOrderDirection,
): T[] {
  if (!canMoveDtoolsRoom(lines, systemIdentity, roomIdentity, direction)) return lines;

  const ordered = sortedBySortOrder(lines);
  const systemIndexes: number[] = [];
  const systemLines: T[] = [];
  for (let i = 0; i < ordered.length; i += 1) {
    const line = ordered[i]!;
    if (dtoolsSystemIdentity(line) !== systemIdentity) continue;
    systemIndexes.push(i);
    systemLines.push(line);
  }

  const segments = buildSystemSegments(systemLines);
  const roomSegmentIndexes = segments
    .map((segment, index) => (segment.kind === "room" ? index : -1))
    .filter((index) => index >= 0);
  const roomIdentities = roomSegmentIndexes.map(
    (index) => (segments[index] as Extract<SystemSegment<T>, { kind: "room" }>).identity,
  );
  const roomOrderIndex = roomIdentities.indexOf(roomIdentity);
  if (roomOrderIndex < 0) return lines;

  const fromSegmentIndex = roomSegmentIndexes[roomOrderIndex]!;
  const toRoomOrderIndex = direction === "up" ? roomOrderIndex - 1 : roomOrderIndex + 1;
  const toSegmentIndex = roomSegmentIndexes[toRoomOrderIndex];
  if (toSegmentIndex == null) return lines;

  const nextSegments = [...segments];
  const tmp = nextSegments[fromSegmentIndex]!;
  nextSegments[fromSegmentIndex] = nextSegments[toSegmentIndex]!;
  nextSegments[toSegmentIndex] = tmp;

  const nextSystemLines = nextSegments.flatMap((segment) => segment.lines);
  if (nextSystemLines.length !== systemIndexes.length) return lines;

  const next = [...ordered];
  for (let i = 0; i < systemIndexes.length; i += 1) {
    next[systemIndexes[i]!] = nextSystemLines[i]!;
  }
  return normaliseDtoolsHierarchySortOrder(next);
}

export function listOrderedDtoolsSystemIdentities<T extends DtoolsHierarchyOrderLine>(
  lines: T[],
): string[] {
  return listSystemIdentitiesInOrder(lines);
}

export function listOrderedDtoolsRoomIdentities<T extends DtoolsHierarchyOrderLine>(
  lines: T[],
  systemIdentity: string,
): string[] {
  return listRoomIdentitiesInOrder(lines, systemIdentity);
}
